/**
 * 読み込んだ DXF からスペース（外形・柱・既存設備・扉）を組み立てる。
 * 座標: mm、y を下向きに反転し、外形の外接矩形の左上が (0, 0) になるよう移動する。
 */
import type { Door, Obstacle, Point, Space } from "@el/schema";
import { formatMm } from "../format";
import { boundingBox, isSelfIntersecting, pointInPolygon, polygonArea, type Rect } from "../geometry";
import { type DxfDocument, type DxfLayer, type DxfShape, UNIT_NAMES } from "./parse";

type Role = DxfLayer["suggestedRole"];
type Note = Space["notes"][number];

const LIMIT_OBSTACLES = 2000;
const LIMIT_DOORS = 200;
const LIMIT_NOTES = 200;
const LIMIT_POLY = 10_000;

function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** 連続する同じ点と、最後の点が最初と同じものを除く */
function cleanRing(pts: Point[], tol: number): Point[] {
  const out: Point[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (last && dist(last, p) <= Math.min(tol, 1e-6)) continue;
    out.push(p);
  }
  while (out.length > 1 && dist(out[0]!, out[out.length - 1]!) <= tol) out.pop();
  return out;
}

type Chain = Point[];

/**
 * 端点が tol 以内の線をつないでいく（決定的: 入力の順に、いちばん近い端点を選ぶ）。
 * 閉じたもの（始点と終点が tol 以内）は loops に、閉じないものは open に入る。
 */
export function joinChains(pieces: Chain[], tol: number): { loops: Chain[]; open: Chain[] } {
  const cell = Math.max(tol, 1) * 2;
  const key = (p: Point) => `${Math.floor(p.x / cell)},${Math.floor(p.y / cell)}`;
  const grid = new Map<string, { idx: number; end: 0 | 1 }[]>();
  const used = new Array<boolean>(pieces.length).fill(false);
  const ends = (i: number, end: 0 | 1) => (end === 0 ? pieces[i]![0]! : pieces[i]![pieces[i]!.length - 1]!);
  pieces.forEach((pc, i) => {
    for (const end of [0, 1] as const) {
      const k = key(ends(i, end));
      const arr = grid.get(k) ?? [];
      arr.push({ idx: i, end });
      grid.set(k, arr);
    }
    void pc;
  });
  const near = (p: Point): { idx: number; end: 0 | 1; d: number } | null => {
    const cx = Math.floor(p.x / cell);
    const cy = Math.floor(p.y / cell);
    let best: { idx: number; end: 0 | 1; d: number } | null = null;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const c of grid.get(`${cx + dx},${cy + dy}`) ?? []) {
          if (used[c.idx]) continue;
          const d = dist(p, ends(c.idx, c.end));
          if (d <= tol + 1e-9 && (!best || d < best.d || (d === best.d && c.idx < best.idx))) best = { ...c, d };
        }
      }
    }
    return best;
  };
  const loops: Chain[] = [];
  const open: Chain[] = [];
  for (let i = 0; i < pieces.length; i++) {
    if (used[i]) continue;
    used[i] = true;
    let chain = pieces[i]!.slice();
    for (;;) {
      if (chain.length > 2 && dist(chain[0]!, chain[chain.length - 1]!) <= tol + 1e-9) break;
      const tail = near(chain[chain.length - 1]!);
      if (tail) {
        used[tail.idx] = true;
        const pc = pieces[tail.idx]!;
        const seg = tail.end === 0 ? pc : pc.slice().reverse();
        chain = chain.concat(seg.slice(1));
        continue;
      }
      const head = near(chain[0]!);
      if (head) {
        used[head.idx] = true;
        const pc = pieces[head.idx]!;
        const seg = head.end === 1 ? pc : pc.slice().reverse();
        chain = seg.slice(0, -1).concat(chain);
        continue;
      }
      break;
    }
    if (chain.length > 2 && dist(chain[0]!, chain[chain.length - 1]!) <= tol + 1e-9) loops.push(chain);
    else open.push(chain);
  }
  return { loops, open };
}

/** 閉じない線を、端点の近い順に（距離を問わず）つないで閉じる。閉じたすき間を返す */
function forceClose(open: Chain[]): { ring: Chain; gaps: { at: Point; length: number }[] } {
  const rest = open.map((c) => c.slice());
  let chain = rest.shift()!;
  const gaps: { at: Point; length: number }[] = [];
  while (rest.length > 0) {
    const tail = chain[chain.length - 1]!;
    let bi = 0;
    let rev = false;
    let bd = Infinity;
    rest.forEach((c, i) => {
      const d0 = dist(tail, c[0]!);
      const d1 = dist(tail, c[c.length - 1]!);
      if (d0 < bd) {
        bd = d0;
        bi = i;
        rev = false;
      }
      if (d1 < bd) {
        bd = d1;
        bi = i;
        rev = true;
      }
    });
    const next = rest.splice(bi, 1)[0]!;
    const seg = rev ? next.reverse() : next;
    if (bd > 0) gaps.push({ at: { x: (tail.x + seg[0]!.x) / 2, y: (tail.y + seg[0]!.y) / 2 }, length: bd });
    chain = chain.concat(seg);
  }
  const a = chain[chain.length - 1]!;
  const b = chain[0]!;
  const d = dist(a, b);
  if (d > 0) gaps.push({ at: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, length: d });
  return { ring: chain, gaps };
}

function rectPoly(r: Rect): Point[] {
  return [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
}

function nearestEdge(outline: Point[], p: Point): { a: Point; b: Point; d: number } | null {
  let best: { a: Point; b: Point; d: number } | null = null;
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    const len2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    if (len2 === 0) continue;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / len2));
    const d = dist(p, { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    if (!best || d < best.d) best = { a, b, d };
  }
  return best;
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000 + 0;
}

function decimate(poly: Point[]): Point[] {
  if (poly.length <= LIMIT_POLY) return poly;
  const step = Math.ceil(poly.length / LIMIT_POLY);
  return poly.filter((_, i) => i % step === 0);
}

export function buildSpaceFromDxf(doc: DxfDocument, roles: Record<string, Role>, opts: { gapToleranceMm?: number; unitsMm?: number } = {}): { space: Space; warnings: string[] } {
  const u = opts.unitsMm && opts.unitsMm > 0 ? opts.unitsMm : doc.unitsMm;
  const tol = opts.gapToleranceMm !== undefined && opts.gapToleranceMm >= 0 ? opts.gapToleranceMm : 1;
  const warnings: string[] = [];
  const notes: Note[] = [];
  const roleOf = (layer: string): Role => roles[layer] ?? doc.layers.find((l) => l.name === layer)?.suggestedRole ?? "ignore";
  const toMm = (p: Point): Point => ({ x: p.x * u, y: -p.y * u });
  const shapesMm = (role: Role) => doc.shapes.filter((s) => roleOf(s.layer) === role).map((s) => ({ ...s, points: s.points.map(toMm) }));

  if (opts.unitsMm === undefined && doc.unitGuessed) {
    const name = UNIT_NAMES[u] ?? `${u} mm`;
    notes.push({ kind: "unit_guessed", message: `図面に単位の記録が無いため、図面の大きさから単位を ${name} と判断しました。` });
  }

  /* 外壁 */
  const walls = shapesMm("wall");
  const loops: Point[][] = [];
  const openPieces: Point[][] = [];
  for (const s of walls) {
    if (s.closed) loops.push(cleanRing(s.points, tol));
    else openPieces.push(s.points);
  }
  const joined = joinChains(openPieces, tol);
  for (const l of joined.loops) loops.push(cleanRing(l, tol));
  // 自己交差した形は面積が 0 に近くなることがあるので、外接矩形の大きさで選ぶ
  const boxArea = (l: Point[]) => {
    const b = boundingBox(l);
    return b.w * b.h;
  };
  const validLoops = loops.filter((l) => l.length >= 3 && boxArea(l) > 0);
  validLoops.sort((a, b) => boxArea(b) - boxArea(a) || polygonArea(b) - polygonArea(a));

  let outline: Point[] | null = validLoops[0] ?? null;
  const openBox = joined.open.length > 0 ? boundingBox(joined.open.flat()) : null;
  const loopBox = outline ? boundingBox(outline) : null;
  const openIsMain = openBox !== null && (!loopBox || openBox.w * openBox.h > loopBox.w * loopBox.h * 1.5);
  if (joined.open.length > 0 && openIsMain) {
    const { ring, gaps } = forceClose(joined.open);
    const cleaned = cleanRing(ring, tol);
    if (cleaned.length >= 3 && boxArea(cleaned) > 0) {
      if (outline) validLoops.unshift(cleaned);
      else validLoops.push(cleaned);
      outline = cleaned;
      for (const g of gaps) {
        notes.push({ kind: "auto_closed", message: `外壁のすき間（約 ${formatMm(Math.round(g.length))}）を直線で閉じました。`, at: g.at });
      }
      if (gaps.length > 0) warnings.push(`外壁が閉じていなかったため、${gaps.length} か所のすき間を直線で閉じました。`);
    }
  } else if (joined.open.length > 0) {
    warnings.push("外壁のレイヤーにある閉じていない線は使いませんでした。");
  }

  if (!outline) {
    const pool = doc.shapes.filter((s) => roleOf(s.layer) !== "ignore");
    const pts = (pool.length > 0 ? pool : doc.shapes).flatMap((s) => s.points.map(toMm));
    const b = boundingBox(pts);
    outline = rectPoly(b);
    notes.push({ kind: "no_wall", message: "外壁が見つからないため、図形全体を囲む長方形をスペースにしました。" });
    warnings.push("外壁が見つからないため、図形全体を囲む長方形をスペースにしました。");
  }
  if (outline.length > LIMIT_POLY) {
    outline = decimate(outline);
    warnings.push("外壁の点が多すぎるため、間引きました。");
  }
  if (isSelfIntersecting(outline)) {
    notes.push({ kind: "self_intersection", message: "外壁の線が交差しています。形を確認してください。" });
    warnings.push("外壁の線が交差しています。形を確認してください。");
  }

  /* 中庭・吹き抜け（外形の内側にある別の閉じた外壁） */
  const holes: Point[][] = [];
  for (const l of validLoops) {
    if (l === outline) continue;
    if (l.every((p) => pointInPolygon(p, outline!))) holes.push(l);
  }

  /* 柱・既存設備・置けない範囲 */
  const obstaclePolys = (role: "column" | "existing" | "zone"): Point[][] => {
    const shapes = shapesMm(role);
    const out: Point[][] = [];
    const groups = new Map<number, DxfShape[]>();
    const loose: DxfShape[] = [];
    for (const s of shapes) {
      if (s.group >= 0) {
        const arr = groups.get(s.group) ?? [];
        arr.push(s);
        groups.set(s.group, arr);
      } else loose.push(s);
    }
    for (const [, g] of [...groups.entries()].sort((a, b) => a[0] - b[0])) {
      const closed = g.filter((s) => s.closed);
      if (closed.length === 1 && g.length === 1) out.push(cleanRing(closed[0]!.points, tol));
      else out.push(rectPoly(boundingBox(g.flatMap((s) => s.points))));
    }
    const openP: Point[][] = [];
    for (const s of loose) {
      if (s.closed) out.push(cleanRing(s.points, tol));
      else openP.push(s.points);
    }
    const j = joinChains(openP, tol);
    for (const l of j.loops) out.push(cleanRing(l, tol));
    for (const o of j.open) {
      const b = boundingBox(o);
      if (b.w > 0 && b.h > 0) out.push(rectPoly(b));
    }
    return out.filter((p) => p.length >= 3 && polygonArea(p) > 0).map(decimate);
  };

  const columns = obstaclePolys("column");
  const existing = obstaclePolys("existing");
  const zones = obstaclePolys("zone");

  /** 名前: 多角形の中にある文字（同じ役割のレイヤー → 寸法以外の無視するレイヤーの順） */
  const textsMm = doc.texts.map((t) => ({ ...t, at: toMm(t.at), role: roleOf(t.layer) }));
  const labelFor = (poly: Point[], role: Role): string | undefined => {
    const pools = [textsMm.filter((t) => t.role === role), textsMm.filter((t) => t.role === "ignore" && !/DIM|寸法/i.test(t.layer))];
    for (const pool of pools) {
      const t = pool.find((x) => pointInPolygon(x.at, poly));
      if (t) return t.text.replace(/\s+/g, " ").trim().slice(0, 100) || undefined;
    }
    return undefined;
  };

  /* 扉 */
  const doorShapes = shapesMm("door");
  const clusters: Rect[] = [];
  const groupBoxes = new Map<number, Rect>();
  for (const s of doorShapes) {
    const b = boundingBox(s.points);
    if (s.group >= 0) {
      const g = groupBoxes.get(s.group);
      groupBoxes.set(s.group, g ? unionRect(g, b) : b);
    } else clusters.push(b);
  }
  clusters.push(...[...groupBoxes.entries()].sort((a, b) => a[0] - b[0]).map(([, r]) => r));
  const merged = mergeTouching(clusters, Math.max(tol, 10));

  /* 移動（外形の外接矩形の左上を原点へ） */
  const ob = boundingBox(outline);
  const shift = (p: Point): Point => ({ x: round3(p.x - ob.x), y: round3(p.y - ob.y) });
  const outlineOut = outline.map(shift);

  const obstacles: Obstacle[] = [];
  let ci = 0;
  let ei = 0;
  let hi = 0;
  for (const h of holes) obstacles.push({ id: `dxf-hole-${++hi}`, kind: "wall", polygon: h.map(shift), label: "吹き抜け・中庭" });
  for (const c of columns) obstacles.push({ id: `dxf-col-${++ci}`, kind: "column", polygon: c.map(shift) });
  let zi = 0;
  for (const e of existing) {
    const label = labelFor(e, "existing");
    obstacles.push({ id: `dxf-ex-${++ei}`, kind: "existing", polygon: e.map(shift), ...(label ? { label } : {}) });
  }
  for (const z of zones) {
    const label = labelFor(z, "zone");
    obstacles.push({ id: `dxf-zone-${++zi}`, kind: "zone", polygon: z.map(shift), ...(label ? { label } : {}) });
  }
  if (obstacles.length > LIMIT_OBSTACLES) {
    warnings.push(`柱・既存設備・置けない範囲が多すぎるため、先頭の ${LIMIT_OBSTACLES} 個だけを使いました。`);
    obstacles.length = LIMIT_OBSTACLES;
  }

  const doors: Door[] = [];
  let di = 0;
  for (const r of merged) {
    const c = { x: r.x + r.w / 2, y: r.y + r.h / 2 };
    const e = nearestEdge(outline, c);
    if (!e) continue;
    const len2 = (e.b.x - e.a.x) ** 2 + (e.b.y - e.a.y) ** 2;
    const ts = rectPoly(r).map((p) => ((p.x - e.a.x) * (e.b.x - e.a.x) + (p.y - e.a.y) * (e.b.y - e.a.y)) / len2);
    const t0 = Math.max(0, Math.min(1, Math.min(...ts)));
    const t1 = Math.max(0, Math.min(1, Math.max(...ts)));
    if ((t1 - t0) * Math.sqrt(len2) < 1) continue;
    const a = { x: e.a.x + (e.b.x - e.a.x) * t0, y: e.a.y + (e.b.y - e.a.y) * t0 };
    const b = { x: e.a.x + (e.b.x - e.a.x) * t1, y: e.a.y + (e.b.y - e.a.y) * t1 };
    doors.push({ id: `dxf-door-${++di}`, a: shift(a), b: shift(b) });
  }
  if (doors.length > LIMIT_DOORS) {
    warnings.push(`扉が多すぎるため、先頭の ${LIMIT_DOORS} 個だけを使いました。`);
    doors.length = LIMIT_DOORS;
  }

  const notesOut = notes.map((n) => (n.at ? { ...n, at: shift(n.at) } : n)).slice(0, LIMIT_NOTES);
  return {
    space: { outline: outlineOut, obstacles, doors, source: "dxf", notes: notesOut, dxfAttachmentId: null },
    warnings,
  };
}

function unionRect(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

function mergeTouching(rects: Rect[], tol: number): Rect[] {
  const out = rects.map((r) => ({ ...r }));
  let changed = true;
  while (changed) {
    changed = false;
    outer: for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i]!;
        const b = out[j]!;
        if (a.x <= b.x + b.w + tol && b.x <= a.x + a.w + tol && a.y <= b.y + b.h + tol && b.y <= a.y + a.h + tol) {
          out[i] = unionRect(a, b);
          out.splice(j, 1);
          changed = true;
          break outer;
        }
      }
    }
  }
  return out;
}
